import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { getUsers, updateUser, deleteUser, createUserAdmin, resetUserPassword, changePassword, getBizumRecipients, updateBizumRecipients, updateCourseCategories as apiUpdateCourseCategories } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit2, Trash2, Key, Users, Lock, Printer, MessageCircle, History, RefreshCw, ChevronDown, ChevronRight, Upload, Waves, Eye, Download, Check, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const RECIPIENT_COLORS = ['#4A6D7C','#81B29A','#E07A5F','#E2B47D','#9B72CF','#5B9BD5','#D47C5A'];
const slugify = (name) => name.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const OP_LABELS = { create: 'CREAR', update: 'EDITAR', delete: 'BORRAR' };
const OP_COLORS = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

export default function SettingsPage() {
  const { updateBizumRecipients: ctxUpdateBizum, courseCategories, updateCourseCategories: ctxUpdateCats } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [newRecipientName, setNewRecipientName] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [savingCategories, setSavingCategories] = useState(false);
  const [waStatus, setWaStatus] = useState({ status: 'loading', qr: null });
  const [activeTab, setActiveTab] = useState('users');

  // Audit log state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  // PDF upload state — 3 tide slots + coeficientes
  const [pdfFiles, setPdfFiles] = useState([null, null, null]);
  const [pdfCoefFile, setPdfCoefFile] = useState(null);
  const [pdfYear, setPdfYear] = useState(new Date().getFullYear());
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfPreview, setPdfPreviewRaw] = useState(() => {
    try { const s = sessionStorage.getItem('tide_pdf_preview'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [tidesSavedDays, setTidesSavedDays] = useState(null);
  const pdfRefs = [useRef(null), useRef(null), useRef(null)];
  const coefPdfRef = useRef(null);

  const setPdfPreview = (val) => {
    setPdfPreviewRaw(val);
    try {
      if (val) sessionStorage.setItem('tide_pdf_preview', JSON.stringify(val));
      else sessionStorage.removeItem('tide_pdf_preview');
    } catch {}
  };

  useEffect(() => {
    api.getTides().then(r => {
      setTidesSavedDays(Object.keys(r.data || {}).length);
    }).catch(() => {});
  }, []);

  const setPdfFile = (idx, file) => {
    setPdfFiles(prev => { const n = [...prev]; n[idx] = file; return n; });
    setPdfPreview(null);
  };

  const parsePdfs = async () => {
    if (!pdfFiles.some(Boolean)) { toast.error('Selecciona al menos un PDF de mareas'); return; }
    setPdfParsing(true);
    try {
      const res = await api.uploadTidePdfs(pdfFiles[0], pdfFiles[1], pdfFiles[2], pdfCoefFile, pdfYear);
      setPdfPreview(res.data);
      if (res.data.days_found === 0) {
        toast.error('0 días parseados — revisa la consola del navegador (F12)');
      } else {
        toast.success(`${res.data.days_found} días parseados${pdfCoefFile ? ' con coeficientes' : ''}`);
      }
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setPdfParsing(false); }
  };

  const importPdfTides = async () => {
    if (!pdfPreview?.parsed || pdfPreview.days_found === 0) { toast.error('Sin días para guardar'); return; }
    setPdfImporting(true);
    try {
      await api.importTides(pdfPreview.parsed);
      setTidesSavedDays(pdfPreview.days_found);
      setPdfPreview(null);
      setPdfFiles([null, null, null]);
      setPdfCoefFile(null);
      pdfRefs.forEach(r => { if (r.current) r.current.value = ''; });
      if (coefPdfRef.current) coefPdfRef.current.value = '';
      toast.success(`Mareas guardadas: ${pdfPreview.days_found} días`);
    } catch (e) {
      toast.error('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally { setPdfImporting(false); }
  };

  const fetchWaStatus = useCallback(async () => {
    try {
      const r = await fetch('/api/whatsapp/status', { credentials: 'include' });
      setWaStatus(await r.json());
    } catch { setWaStatus({ status: 'service_unavailable', qr: null }); }
  }, []);
  useEffect(() => { fetchWaStatus(); const iv = setInterval(fetchWaStatus, 5000); return () => clearInterval(iv); }, [fetchWaStatus]);

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      await fetch('/api/whatsapp/reconnect', { method: 'POST', credentials: 'include' });
      setTimeout(fetchWaStatus, 4000);
    } catch { toast.error('Error al reconectar'); }
    finally { setTimeout(() => setReconnecting(false), 4000); }
  };

  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const r = await fetch('/api/audit/sessions', { credentials: 'include' });
      const data = await r.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); setSessions([]); }
    finally { setLoadingSessions(false); }
  }, []);

  useEffect(() => { if (activeTab === 'historial') fetchSessions(); }, [activeTab, fetchSessions]);


  const [dialogOpen, setDialogOpen] = useState(false);
  const [pwDialog, setPwDialog] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'monitor' });
  const [newPw, setNewPw] = useState('');
  const [changePwForm, setChangePwForm] = useState({ current_password: '', new_password: '' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const { data } = await getUsers(); setUsers(data); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    getBizumRecipients().then(({ data }) => { if (Array.isArray(data)) setRecipients(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (Array.isArray(courseCategories)) setCategories(courseCategories);
  }, [courseCategories]);

  const addCategory = () => {
    const name = newCategoryInput.trim().toLowerCase();
    if (!name || categories.includes(name)) { setNewCategoryInput(''); return; }
    setCategories(prev => [...prev, name]);
    setNewCategoryInput('');
  };

  const removeCategory = (cat) => {
    if (cat === 'surf' || cat === 'skate') { toast.error('Las categorías base no se pueden eliminar'); return; }
    setCategories(prev => prev.filter(c => c !== cat));
  };

  const saveCategories = async () => {
    setSavingCategories(true);
    try {
      await apiUpdateCourseCategories(categories);
      ctxUpdateCats(categories);
      toast.success('Categorías guardadas');
    } catch { toast.error('Error guardando categorías'); }
    finally { setSavingCategories(false); }
  };

  const addRecipient = () => {
    const name = newRecipientName.trim();
    if (!name) return;
    const id = slugify(name);
    if (recipients.some(r => r.id === id)) { toast.error('Ya existe un receptor con ese nombre'); return; }
    const color = RECIPIENT_COLORS[recipients.length % RECIPIENT_COLORS.length];
    setRecipients(prev => [...prev, { id, name, color }]);
    setNewRecipientName('');
  };

  const removeRecipient = (id) => setRecipients(prev => prev.filter(r => r.id !== id));

  const saveRecipients = async () => {
    setSavingRecipients(true);
    try {
      await updateBizumRecipients(recipients);
      ctxUpdateBizum(recipients);
      toast.success('Receptores Bizum guardados');
    } catch { toast.error('Error guardando receptores'); }
    finally { setSavingRecipients(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser._id, { name: userForm.name, email: userForm.email, role: userForm.role });
        toast.success('Usuario actualizado');
      } else {
        await createUserAdmin({ name: userForm.name, email: userForm.email, password: userForm.password });
        toast.success('Usuario creado');
      }
      setDialogOpen(false); setEditingUser(null); setUserForm({ name: '', email: '', password: '', role: 'monitor' }); fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleEditUser = (u) => { setEditingUser(u); setUserForm({ name: u.name, email: u.email, password: '', role: u.role }); setDialogOpen(true); };
  const handleDeleteUser = async (id) => { if (!window.confirm('¿Eliminar usuario?')) return; try { await deleteUser(id); toast.success('Eliminado'); fetchUsers(); } catch { toast.error('Error'); } };
  const handleResetPw = async () => { try { await resetUserPassword(pwDialog, newPw); toast.success('Contraseña reseteada'); setPwDialog(null); setNewPw(''); } catch { toast.error('Error'); } };
  const handleChangePw = async (e) => { e.preventDefault(); try { await changePassword(changePwForm); toast.success('Contraseña cambiada'); setChangePwForm({ current_password: '', new_password: '' }); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };

  const getRoleBadge = (role) => {
    const colors = { admin: 'bg-[#E07A5F]/15 text-[#E07A5F]', caseta: 'bg-[#E2B47D]/15 text-[#E2B47D]', monitor: 'bg-[#4A6D7C]/15 text-[#4A6D7C]' };
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[role] || colors.monitor}`}>{role}</span>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="sticky top-0 z-10 bg-[#FCFBF9] pt-4 md:pt-8 pb-3 space-y-3">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Ajustes</h1>
          <TabsList className="bg-[#F5F2ED] flex-wrap h-auto gap-1">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><Users className="w-4 h-4 mr-1.5"/>Usuarios</TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><Lock className="w-4 h-4 mr-1.5"/>Mi Contraseña</TabsTrigger>
            <TabsTrigger value="printer" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><Printer className="w-4 h-4 mr-1.5"/>Impresora</TabsTrigger>
            <TabsTrigger value="whatsapp" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><MessageCircle className="w-4 h-4 mr-1.5"/>WhatsApp</TabsTrigger>
            <TabsTrigger value="historial" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><History className="w-4 h-4 mr-1.5"/>Historial</TabsTrigger>
            <TabsTrigger value="mareas-pdf" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><Upload className="w-4 h-4 mr-1.5"/>Mareas PDF</TabsTrigger>
            <TabsTrigger value="school" className="data-[state=active]:bg-[#4A6D7C] data-[state=active]:text-white"><Building2 className="w-4 h-4 mr-1.5"/>Mi Escuela</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingUser(null); setUserForm({ name: '', email: '', password: '', role: 'monitor' }); setDialogOpen(true); }} data-testid="add-user-btn" className="bg-[#4A6D7C] hover:bg-[#3A5663] text-white"><Plus className="w-4 h-4 mr-2"/>Nuevo Usuario</Button>
          </div>
          <div className="border border-[#E5E0D8] rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-[#F5F2ED]">
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nombre</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Rol</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8] text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={4}><div className="skeleton-pulse h-6 rounded"/></TableCell></TableRow>) :
                users.map((u, i) => (
                  <TableRow key={u._id} className="hover:bg-[#F5F2ED]/50" data-testid={`user-row-${i}`}>
                    <TableCell className="text-sm font-medium text-[#1A2B3C]">{u.name}</TableCell>
                    <TableCell className="text-sm text-[#4A5D6C]">{u.email}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => handleEditUser(u)} className="h-8 w-8 text-[#4A6D7C]"><Edit2 className="w-3.5 h-3.5"/></Button>
                        <Button variant="ghost" size="icon" onClick={() => { setPwDialog(u._id); setNewPw(''); }} className="h-8 w-8 text-[#E2B47D]"><Key className="w-3.5 h-3.5"/></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u._id)} className="h-8 w-8 text-[#E07A5F]"><Trash2 className="w-3.5 h-3.5"/></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="password" className="mt-6">
          <div className="max-w-md">
            <form onSubmit={handleChangePw} className="space-y-4">
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Contraseña Actual</Label><Input type="password" value={changePwForm.current_password} onChange={e => setChangePwForm({...changePwForm, current_password: e.target.value})} required className="border-[#E5E0D8]"/></div>
              <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nueva Contraseña</Label><Input type="password" value={changePwForm.new_password} onChange={e => setChangePwForm({...changePwForm, new_password: e.target.value})} required className="border-[#E5E0D8]"/></div>
              <Button type="submit" className="bg-[#4A6D7C] hover:bg-[#3A5663] text-white">Cambiar Contraseña</Button>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="printer" className="mt-6">
          <div className="max-w-md space-y-6">
            <div>
              <h3 className="text-lg font-bold text-[#1A2B3C] mb-2" style={{ fontFamily: 'Manrope' }}>Configuración de Impresora</h3>
              <p className="text-sm text-[#8A9BA8] mb-4">La impresión utiliza el diálogo de impresión del navegador. Puedes configurar tu impresora predeterminada desde las opciones del sistema.</p>
            </div>
            <Button onClick={() => { window.print(); }} className="bg-[#4A6D7C] hover:bg-[#3A5663] text-white" data-testid="test-print-btn"><Printer className="w-4 h-4 mr-2"/>Imprimir Página de Prueba</Button>
            <div className="bg-[#F5F2ED] rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-bold text-[#1A2B3C]">Instrucciones:</h4>
              <ul className="text-sm text-[#4A5D6C] space-y-1 list-disc pl-4">
                <li>En cada factura aparece el botón de imprimir (icono de impresora)</li>
                <li>Al pulsar, se abrirá el PDF con el diálogo de impresión del navegador</li>
                <li>Selecciona tu impresora en el diálogo del sistema</li>
                <li>Para configurar la impresora predeterminada, ve a Ajustes del Sistema → Impresoras</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-6">
          <div className="max-w-md space-y-4">
            <div>
              <h3 className="text-lg font-bold text-[#1A2B3C] mb-1" style={{ fontFamily: 'Manrope' }}>WhatsApp</h3>
              <p className="text-sm text-[#8A9BA8]">Conecta tu WhatsApp para enviar notificaciones de cursos a las reservas.</p>
            </div>
            {waStatus.status === 'loading' && <p className="text-sm text-[#8A9BA8]">Conectando con el servicio...</p>}
            {waStatus.status === 'service_unavailable' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 font-medium">Servicio no disponible</p>
                <p className="text-xs text-red-500 mt-1">El servicio WhatsApp no está activo en el servidor.</p>
              </div>
            )}
            {waStatus.status === 'connected' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                  <p className="text-sm text-green-700 font-medium">WhatsApp conectado ✓</p>
                </div>
                <Button size="sm" variant="outline" onClick={handleReconnect} disabled={reconnecting} className="border-green-300 text-green-700 hover:bg-green-100 text-xs">
                  <RefreshCw className={`w-3 h-3 mr-1.5 ${reconnecting ? 'animate-spin' : ''}`} />
                  {reconnecting ? 'Reconectando...' : 'Reconectar'}
                </Button>
              </div>
            )}
            {(waStatus.status === 'qr' || waStatus.status === 'disconnected') && waStatus.qr && (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700 font-medium">Escanea el QR con tu WhatsApp</p>
                  <p className="text-xs text-amber-600 mt-1">WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
                </div>
                <img src={waStatus.qr} alt="QR WhatsApp" className="border-2 border-[#E5E0D8] rounded-lg" style={{ width:240, height:240, objectFit:'contain' }} />
              </div>
            )}
            {(waStatus.status === 'qr' || waStatus.status === 'disconnected') && !waStatus.qr && (
              <div className="bg-[#F5F2ED] border border-[#E5E0D8] rounded-lg p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-[#4A6D7C] font-medium">Sesión desconectada</p>
                  <p className="text-xs text-[#8A9BA8] mt-1">Pulsa reconectar para restablecer la sesión.</p>
                </div>
                <Button size="sm" onClick={handleReconnect} disabled={reconnecting} className="bg-[#4A6D7C] hover:bg-[#3a5a6a] text-white text-xs">
                  <RefreshCw className={`w-3 h-3 mr-1.5 ${reconnecting ? 'animate-spin' : ''}`} />
                  {reconnecting ? 'Reconectando...' : 'Reconectar'}
                </Button>
              </div>
            )}
            <Button onClick={fetchWaStatus} variant="outline" className="border-[#E5E0D8] text-[#4A6D7C] hover:bg-[#F5F2ED]">
              <MessageCircle className="w-4 h-4 mr-2" />Actualizar estado
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="historial" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Historial de Sesiones</h3>
                <p className="text-sm text-[#8A9BA8] mt-0.5">Últimas 20 sesiones con los cambios efectuados. Puedes revertir cualquier cambio individual.</p>
              </div>
              <Button variant="outline" onClick={fetchSessions} disabled={loadingSessions} className="border-[#E5E0D8] text-[#4A6D7C]">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingSessions ? 'animate-spin' : ''}`}/>Actualizar
              </Button>
            </div>

            {loadingSessions ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton-pulse h-16 rounded-xl"/>)}</div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12 text-[#8A9BA8]">
                <History className="w-10 h-10 mx-auto mb-3 opacity-40"/>
                <p className="text-sm">No hay sesiones registradas todavía</p>
                <p className="text-xs mt-1">Las sesiones aparecerán después del próximo login</p>
              </div>
            ) : sessions.map((s, si) => {
              const isExpanded = expandedSession === s.id;
              const changes = s.changes || [];
              return (
                <div key={s.id} className="border border-[#E5E0D8] rounded-xl overflow-hidden shadow-sm">
                  <button className="w-full flex items-center justify-between p-4 bg-[#F5F2ED] hover:bg-[#EBE7E0] transition-colors text-left"
                    onClick={() => setExpandedSession(isExpanded ? null : s.id)}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#4A6D7C] text-white text-sm font-bold flex items-center justify-center">
                        {s.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#1A2B3C] truncate">{s.user_name} <span className="font-normal text-[#8A9BA8]">({s.user_email})</span></div>
                        <div className="text-xs text-[#8A9BA8]">
                          {fmtDT(s.login_at)}
                          {s.ip && s.ip !== 'unknown' && <span className="ml-2 opacity-60">· IP {s.ip}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${changes.length > 0 ? 'bg-[#4A6D7C]/10 text-[#4A6D7C]' : 'bg-[#F5F2ED] text-[#8A9BA8]'}`}>
                        {changes.length} cambio{changes.length !== 1 ? 's' : ''}
                      </span>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-[#8A9BA8]"/> : <ChevronRight className="w-4 h-4 text-[#8A9BA8]"/>}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[#E5E0D8] bg-white p-4">
                      {changes.length === 0 ? (
                        <p className="text-sm text-[#8A9BA8] italic text-center py-4">Sin cambios registrados en esta sesión</p>
                      ) : (
                        <div className="space-y-2">
                          {changes.map((ch, ci) => (
                            <div key={ci} className="flex items-center justify-between p-3 bg-[#FCFBF9] border border-[#E5E0D8] rounded-lg gap-3">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${OP_COLORS[ch.op] || 'bg-gray-100 text-gray-600'}`}>
                                  {OP_LABELS[ch.op] || ch.op}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-sm text-[#1A2B3C] font-medium">{ch.description}</p>
                                  <p className="text-xs text-[#8A9BA8]">{fmtDT(ch.at)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="mareas-pdf" className="mt-6">
          <div className="max-w-2xl space-y-5">

            <div className="bg-[#F0F7F4] border border-[#81B29A]/30 rounded-2xl p-4 space-y-1.5">
              <h3 className="font-semibold text-[#1A2B3C] flex items-center gap-2"><Upload className="w-4 h-4 text-[#4A6D7C]"/>Subir PDFs de la Armada</h3>
              <p className="text-sm text-[#4A5D6C]">La Armada publica 3 PDFs de mareas por puerto (Ene-Abr, May-Ago, Sep-Dic). Sube cada uno en su slot correspondiente.</p>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 space-y-5">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Año</label>
                  <input type="number" min="2024" max="2035" value={pdfYear} onChange={e => setPdfYear(parseInt(e.target.value))} className="border border-[#E5E0D8] rounded-md px-3 py-1.5 text-sm w-24" />
                </div>
                {tidesSavedDays !== null && tidesSavedDays > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[#81B29A] font-medium mt-5">
                    <Check className="w-4 h-4" /> {tidesSavedDays} días guardados en DB
                  </div>
                )}
                {tidesSavedDays === 0 && (
                  <div className="text-sm text-[#8A9BA8] mt-5">Sin mareas en DB</div>
                )}
              </div>

              {[
                { label: 'PDF 1 — Enero · Febrero · Marzo · Abril', hint: 'PDF mareas 1.pdf' },
                { label: 'PDF 2 — Mayo · Junio · Julio · Agosto',   hint: 'PDF mareas 2.pdf' },
                { label: 'PDF 3 — Sep · Oct · Nov · Dic',           hint: 'PDF mareas 3.pdf' },
              ].map((slot, idx) => {
                const file = pdfFiles[idx];
                return (
                  <div key={idx} className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">{slot.label}</label>
                    <div
                      onClick={() => pdfRefs[idx].current?.click()}
                      className={`flex items-center gap-3 border-2 border-dashed rounded-xl p-3 cursor-pointer transition-colors ${file ? 'border-[#4A6D7C] bg-[#4A6D7C]/5' : 'border-[#E5E0D8] hover:border-[#4A6D7C]/40 hover:bg-[#F5F2ED]'}`}
                    >
                      <Upload className={`w-4 h-4 flex-shrink-0 ${file ? 'text-[#4A6D7C]' : 'text-[#C5BFB8]'}`} />
                      <div className="min-w-0 flex-1">
                        {file
                          ? <p className="text-sm font-medium text-[#1A2B3C] truncate">{file.name} <span className="text-[#8A9BA8] font-normal">({(file.size/1024).toFixed(0)} KB)</span></p>
                          : <p className="text-sm text-[#8A9BA8]">{slot.hint} — haz clic para seleccionar</p>
                        }
                      </div>
                      {file && <button onClick={e => { e.stopPropagation(); setPdfFile(idx, null); if (pdfRefs[idx].current) pdfRefs[idx].current.value=''; }} className="text-[#8A9BA8] hover:text-[#E07A5F]"><span className="text-lg leading-none">×</span></button>}
                    </div>
                    <input ref={pdfRefs[idx]} type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => setPdfFile(idx, e.target.files[0] || null)} />
                  </div>
                );
              })}

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">PDF Coeficientes <span className="font-normal normal-case text-[#8A9BA8]">(recomendado)</span></label>
                <div
                  onClick={() => coefPdfRef.current?.click()}
                  className={`flex items-center gap-3 border-2 border-dashed rounded-xl p-3 cursor-pointer transition-colors ${pdfCoefFile ? 'border-[#81B29A] bg-[#81B29A]/5' : 'border-[#E5E0D8] hover:border-[#81B29A]/40 hover:bg-[#F5F2ED]'}`}
                >
                  <Upload className={`w-4 h-4 flex-shrink-0 ${pdfCoefFile ? 'text-[#81B29A]' : 'text-[#C5BFB8]'}`} />
                  <div className="min-w-0 flex-1">
                    {pdfCoefFile
                      ? <p className="text-sm font-medium text-[#1A2B3C] truncate">{pdfCoefFile.name} <span className="text-[#8A9BA8] font-normal">({(pdfCoefFile.size/1024).toFixed(0)} KB)</span></p>
                      : <p className="text-sm text-[#8A9BA8]">Coeficientes PDF</p>
                    }
                  </div>
                  {pdfCoefFile && <button onClick={e => { e.stopPropagation(); setPdfCoefFile(null); if (coefPdfRef.current) coefPdfRef.current.value=''; }} className="text-[#8A9BA8] hover:text-[#E07A5F]"><span className="text-lg leading-none">×</span></button>}
                </div>
                <input ref={coefPdfRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => { setPdfCoefFile(e.target.files[0] || null); setPdfPreview(null); }} />
              </div>

              <div className="flex gap-3 flex-wrap items-center">
                <Button onClick={parsePdfs} disabled={pdfParsing || !pdfFiles.some(Boolean)} className="bg-[#4A6D7C] text-white hover:bg-[#3A5663]">
                  <Eye className="w-4 h-4 mr-2" />{pdfParsing ? 'Leyendo PDFs…' : 'Añadir PDFs'}
                </Button>
                {pdfPreview?.parsed && pdfPreview.days_found > 0 && (
                  <Button onClick={importPdfTides} disabled={pdfImporting} className="bg-[#81B29A] text-white hover:bg-[#6A9B83]">
                    <Download className="w-4 h-4 mr-2" />{pdfImporting ? 'Guardando…' : `Guardar ${pdfPreview.days_found} días`}
                  </Button>
                )}
                {tidesSavedDays > 0 && !pdfPreview && (
                  <a href="/schedule" className="text-sm text-[#4A6D7C] underline underline-offset-2 hover:text-[#3A5663]">
                    Ver en Programación →
                  </a>
                )}
              </div>

              {pdfPreview?.days_found === 0 && (
                <p className="text-sm text-[#E07A5F]">⚠ 0 días parseados — abre la consola del navegador (F12 → Consola) para ver el texto extraído del PDF y reportarlo.</p>
              )}
            </div>

            {pdfPreview?.parsed && pdfPreview.days_found > 0 && (
              <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-[#1A2B3C]">{pdfPreview.days_found} días parseados</h4>
                    <span className="text-xs text-[#8A9BA8]">Mareas (hora UTC)</span>
                  </div>
                  <span className="text-xs text-[#8A9BA8]">
                    {pdfCoefFile ? `${Object.values(pdfPreview.parsed).filter(d => d.coefficient !== 60).length} con coef real` : 'coef: valor por defecto'}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-[#E5E0D8]">
                        <th className="text-left py-2 pr-4 text-xs font-semibold uppercase tracking-wider text-[#8A9BA8]">Fecha</th>
                        <th className="text-center py-2 pr-4 text-xs font-semibold uppercase tracking-wider text-[#8A9BA8]">Coef</th>
                        <th className="text-left py-2 text-xs font-semibold uppercase tracking-wider text-[#8A9BA8]">Mareas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(pdfPreview.parsed).sort(([a],[b]) => a.localeCompare(b)).map(([date, info]) => (
                        <tr key={date} className="border-b border-[#F5F2ED] hover:bg-[#FCFBF9]">
                          <td className="py-1.5 pr-4 font-mono text-xs text-[#1A2B3C]">{date}</td>
                          <td className="py-1.5 pr-4 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${info.coefficient >= 80 ? 'bg-blue-100 text-blue-700' : info.coefficient >= 50 ? 'bg-green-100 text-green-700' : info.coefficient === 60 ? 'bg-gray-100 text-gray-400' : 'bg-amber-100 text-amber-700'}`}>{info.coefficient}</span>
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap gap-1.5">
                              {info.tides.map((t, i) => (
                                <span key={i} className={`text-xs px-1.5 py-0.5 rounded font-mono ${t.type === 'pleamar' ? 'bg-[#4A6D7C]/10 text-[#4A6D7C]' : 'bg-[#E07A5F]/10 text-[#E07A5F]'}`}>
                                  {t.time} {t.height}m {t.type === 'pleamar' ? '↑' : '↓'}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="school" className="mt-6">
          <div className="max-w-lg space-y-6">
            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-[#1A2B3C]">Receptores de Bizum</h3>
                <p className="text-xs text-[#8A9BA8] mt-1">Personas que reciben pagos por Bizum. Aparecen como columnas en Excel Skull y como opciones de cobro.</p>
              </div>
              <div className="space-y-2">
                {recipients.map(r => (
                  <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#E5E0D8] bg-[#FAFAF8]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: r.color }} />
                    <span className="flex-1 text-sm font-medium text-[#1A2B3C]">{r.name}</span>
                    <span className="text-xs text-[#8A9BA8] font-mono">bizum_{r.id}</span>
                    <button onClick={() => removeRecipient(r.id)} className="p-1 rounded text-[#E07A5F] hover:bg-[#E07A5F]/10 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {recipients.length === 0 && (
                  <p className="text-sm text-[#C5BFB8] text-center py-3">Sin receptores. Añade el primero.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newRecipientName}
                  onChange={e => setNewRecipientName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addRecipient()}
                  placeholder="Nombre del receptor (ej: María)"
                  className="border-[#E5E0D8] flex-1"
                />
                <Button variant="outline" className="border-[#E5E0D8]" onClick={addRecipient}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={saveRecipients} disabled={savingRecipients} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">
                {savingRecipients ? 'Guardando...' : 'Guardar receptores'}
              </Button>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-[#1A2B3C]">Categorías de Cursos</h3>
                <p className="text-xs text-[#8A9BA8] mt-1">Tipos de curso disponibles. Surf y Skate son fijas y no se pueden eliminar.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => {
                  const isBase = cat === 'surf' || cat === 'skate';
                  return (
                    <div key={cat} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E0D8] bg-[#FAFAF8]">
                      <span className="text-sm font-medium text-[#1A2B3C] capitalize">{cat}</span>
                      {!isBase && (
                        <button onClick={() => removeCategory(cat)} className="text-[#C5BFB8] hover:text-[#E07A5F] transition-colors">
                          <span className="text-base leading-none">×</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newCategoryInput}
                  onChange={e => setNewCategoryInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCategory()}
                  placeholder="Nueva categoría (ej: sup)"
                  className="border-[#E5E0D8] flex-1"
                />
                <Button variant="outline" className="border-[#E5E0D8]" onClick={addCategory}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={saveCategories} disabled={savingCategories} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">
                {savingCategories ? 'Guardando...' : 'Guardar categorías'}
              </Button>
            </div>
          </div>
        </TabsContent>

      </Tabs>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] w-[92vw] sm:w-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>{editingUser ? 'Editar' : 'Nuevo'} Usuario</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-2">
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nombre</Label><Input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} required className="border-[#E5E0D8]"/></div>
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</Label><Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} required className="border-[#E5E0D8]"/></div>
            {!editingUser && <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Contraseña</Label><Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} required className="border-[#E5E0D8]"/></div>}
            {editingUser && <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Rol</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}><SelectTrigger className="border-[#E5E0D8]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="caseta">Caseta</SelectItem><SelectItem value="monitor">Monitor</SelectItem></SelectContent></Select>
            </div>}
            <Button type="submit" className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">{editingUser ? 'Guardar' : 'Crear'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!pwDialog} onOpenChange={() => setPwDialog(null)}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] w-[92vw] sm:w-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]">Resetear Contraseña</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nueva Contraseña</Label><Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="border-[#E5E0D8]"/></div>
            <Button onClick={handleResetPw} className="w-full bg-[#4A6D7C] hover:bg-[#3A5663] text-white">Resetear</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
